import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Avatar,
  Box,
  Container,
  Flex,
  Grid,
  Heading,
  Skeleton,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { MdVideoLibrary } from "react-icons/md";
import Layout from "../../../components/student/Layout/layout";
import VideoCard from "../../../containers/student/video/video";
import AddVoucher from "../../../containers/student/models/add-voucher";
import { getSectionRequest } from "../../../modules/students-modules/sections/Actions";
import { getVideosByChapterIdSuccess } from "../../../modules/students-modules/videos/Actions";
import axiosInstance from "../../../helpers/api";
import { buildStudentVideoPath } from "../../../helpers/studentVideoLink";
import defaultLogo from "../../../assets/logo.png";

const C = {
  primary: "#E85D04",
  secondary: "#9A3412",
  border: "#E8E0D5",
  paper: "#FFFFFF",
  mutedBg: "#F5F0E8",
  muted: "#78716C",
};

const BASE_URL = "https://asquera.com/asquera";

function chapterLogoUrl(logo) {
  if (!logo || logo === "null" || logo === "NULL" || logo === "undefined") {
    return defaultLogo;
  }
  if (String(logo).startsWith("http")) return logo;
  return `${BASE_URL}/${String(logo).replace(/^\//, "")}`;
}

const sameId = (a, b) => String(a) === String(b);

const VideosChaptersList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const section_name = searchParams.get("name");
  const chapterFromUrl = searchParams.get("chapter");
  const navigate = useNavigate();
  const { id } = useParams();
  const section = useSelector((state) => state.sections.section);
  const isLoading = useSelector((state) => state.sections.isLoading);
  const [expandedChapters, setExpandedChapters] = useState([]);
  const [allVideos, setAllVideos] = useState({});
  const [loadingChapters, setLoadingChapters] = useState(new Set());
  const [selectedVideoId, setSelectedVideoId] = useState(null);
  const restoredForSectionRef = useRef(null);
  const allVideosRef = useRef({});

  const dispatch = useDispatch();

  const fetchChapterVideos = useCallback(
    async (chapterId) => {
      const cached =
        allVideosRef.current[chapterId] ||
        allVideosRef.current[String(chapterId)];
      if (cached) {
        dispatch(getVideosByChapterIdSuccess({ data: cached }));
        return;
      }

      try {
        setLoadingChapters((prev) => new Set(prev).add(chapterId));
        const response = await axiosInstance.get(
          `/students/videos/${chapterId}`
        );
        const videos = response.data.data || [];
        allVideosRef.current = {
          ...allVideosRef.current,
          [chapterId]: videos,
        };
        setAllVideos(allVideosRef.current);
        dispatch(getVideosByChapterIdSuccess({ data: videos }));
      } catch (error) {
        console.error(
          `Error fetching videos for chapter ${chapterId}:`,
          error
        );
        allVideosRef.current = {
          ...allVideosRef.current,
          [chapterId]: [],
        };
        setAllVideos(allVideosRef.current);
        dispatch(getVideosByChapterIdSuccess({ data: [] }));
      } finally {
        setLoadingChapters((prev) => {
          const newSet = new Set(prev);
          newSet.delete(chapterId);
          return newSet;
        });
      }
    },
    [dispatch]
  );

  const persistChapterInUrl = useCallback(
    (chapterId) => {
      const next = new URLSearchParams(searchParams);
      if (chapterId != null && chapterId !== "") {
        next.set("chapter", String(chapterId));
      } else {
        next.delete("chapter");
      }
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  useEffect(() => {
    if (id) {
      restoredForSectionRef.current = null;
      allVideosRef.current = {};
      setAllVideos({});
      setExpandedChapters([]);
      setLoadingChapters(new Set());
      dispatch(getSectionRequest(id));
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (!id || isLoading || !section?.length) return;
    if (restoredForSectionRef.current === id) return;
    restoredForSectionRef.current = id;
    if (!chapterFromUrl) return;
    const chapter = section.find((ch) => sameId(ch.id, chapterFromUrl));
    if (!chapter) return;
    setExpandedChapters([chapter.id]);
    fetchChapterVideos(chapter.id);
  }, [section, id, isLoading, chapterFromUrl, fetchChapterVideos]);

  const handleAccordionChange = (indexes) => {
    const indexArr = Array.isArray(indexes) ? indexes : [indexes];
    const nextIds = indexArr
      .map((idx) => section?.[idx]?.id)
      .filter((chapterId) => chapterId != null);

    const newlyOpened = nextIds.filter(
      (chapterId) =>
        !expandedChapters.some((expandedId) => sameId(expandedId, chapterId))
    );

    setExpandedChapters(nextIds);
    newlyOpened.forEach((chapterId) => {
      fetchChapterVideos(chapterId);
    });

    persistChapterInUrl(
      nextIds.length ? nextIds[nextIds.length - 1] : ""
    );
  };

  const handleVideoClick = (videoData, chapter) => {
    setSelectedVideoId(videoData?.id);
    navigate(
      buildStudentVideoPath({
        videoId: videoData?.id,
        chapterId: chapter?.id,
        chapterName: chapter?.name,
        sectionId: id,
        sectionName: section_name,
      })
    );
  };

  const expandedIndexes = (section || []).reduce((acc, chapter, index) => {
    if (expandedChapters.some((chapterId) => sameId(chapterId, chapter.id))) {
      acc.push(index);
    }
    return acc;
  }, []);

  const renderVideoContent = (chapterId) => {
    const isExpanded = expandedChapters.some((expandedId) =>
      sameId(expandedId, chapterId)
    );
    const chapterVideos =
      allVideos[chapterId] || allVideos[String(chapterId)] || [];
    const isLoadingChapter = loadingChapters.has(chapterId);
    const chapter = section?.find((ch) => sameId(ch.id, chapterId));

    if (!isExpanded) return null;

    if (isLoadingChapter) {
      return (
        <Grid
          templateColumns={{ base: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }}
          gap={4}
          p={5}
        >
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} h="280px" borderRadius="xl" />
          ))}
        </Grid>
      );
    }

    if (chapterVideos.length > 0) {
      return (
        <Box p={5}>
          <Grid
            templateColumns={{ base: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }}
            gap={4}
          >
            {chapterVideos.map((video, idx) => (
              <VideoCard
                key={video?.id ?? idx}
                data={video}
                index={idx}
                isSelected={selectedVideoId === video?.id}
                handelClick={() => handleVideoClick(video, chapter)}
              />
            ))}
          </Grid>
        </Box>
      );
    }

    return (
      <Flex direction="column" align="center" justify="center" p={8} color={C.muted} gap={2}>
        <MdVideoLibrary size={32} />
        <Text fontWeight="600" fontSize="sm" textAlign="center">
          You Didn&apos;t pay for this chapter or this chapter has no videos
        </Text>
      </Flex>
    );
  };

  return (
    <Layout color="main_1" bg="white" isLoading={isLoading}>
      <Container maxW="container.2xl" h="100%" my="100px" mb="165px" w="100%">
        <VStack spacing={8} h="100%" w="100%" alignItems="stretch">
          <Box w="100%" maxW="4xl" mx="auto">
            <Flex
              align="center"
              justify="space-between"
              borderBottomWidth="2px"
              borderColor={C.primary}
              pb={4}
              mb={8}
              flexWrap="wrap"
              gap={4}
            >
              <Heading
                fontSize={{ base: "xl", md: "2xl" }}
                fontWeight="800"
                color={C.primary}
                textTransform="uppercase"
                letterSpacing="tight"
              >
                {section_name || "Videos"}
              </Heading>
            </Flex>

            {isLoading && (!section || section.length === 0) ? (
              <VStack spacing={4} align="stretch">
                {[1, 2, 3].map((k) => (
                  <Skeleton key={k} h="72px" borderRadius="xl" />
                ))}
              </VStack>
            ) : !section?.length ? (
              <Text color={C.muted} fontWeight="600">
                No chapters available for this section yet.
              </Text>
            ) : (
              <Accordion
                allowMultiple
                index={expandedIndexes}
                onChange={handleAccordionChange}
              >
                {section.map((chapter, index) => (
                  <AccordionItem
                    key={chapter?.id}
                    bg={C.paper}
                    borderWidth="1px"
                    borderColor={C.border}
                    borderRadius="xl"
                    mb={4}
                    overflow="hidden"
                  >
                    <AccordionButton
                      px={6}
                      py={5}
                      _hover={{ bg: C.mutedBg }}
                      borderBottomWidth="1px"
                      borderColor={C.border}
                      disabled={loadingChapters.has(chapter?.id)}
                      opacity={loadingChapters.has(chapter?.id) ? 0.6 : 1}
                    >
                      <Flex flex={1} align="center" gap={4} textAlign="left">
                        <Avatar
                          name={chapter?.name}
                          src={chapterLogoUrl(chapter?.logo)}
                          borderRadius="lg"
                          size="md"
                          bg="transparent"
                          flexShrink={0}
                        />
                        <Text flex={1} fontWeight="700" fontSize="md" color={C.primary}>
                          {chapter?.name || `Chapter ${index + 1}`}
                        </Text>
                        {loadingChapters.has(chapter?.id) ? (
                          <Spinner size="sm" color={C.primary} />
                        ) : null}
                      </Flex>
                      <AccordionIcon color={C.primary} />
                    </AccordionButton>
                    <AccordionPanel p={0}>{renderVideoContent(chapter?.id)}</AccordionPanel>
                  </AccordionItem>
                ))}
              </Accordion>
            )}
          </Box>
        </VStack>
      </Container>
      <AddVoucher video_id={selectedVideoId} />
    </Layout>
  );
};

export default VideosChaptersList;
